import React from 'react';
import { X, Loader2, AlertTriangle, HelpCircle } from 'lucide-react';

interface ConfirmationModalProps {
    isOpen: boolean;
    title: string;
    description: string;
    // Optional rich block rendered under the description — a read-only summary
    // of what is about to happen (amounts, names, counts), for confirmations
    // where a sentence cannot carry the facts the approver needs to check.
    details?: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    loading?: boolean;
    onConfirm: () => void;
    onClose: () => void;
    tone?: 'primary' | 'danger';
    // Optional free-text field rendered below the description. When onCommentChange is
    // provided, a textarea is shown (label/placeholder optional).
    commentValue?: string;
    onCommentChange?: (v: string) => void;
    commentLabel?: string;
    commentPlaceholder?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    title,
    description,
    details,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    loading = false,
    onConfirm,
    onClose,
    tone = 'primary',
    commentValue,
    onCommentChange,
    commentLabel = 'Comment (optional)',
    commentPlaceholder = 'Add a note…',
}) => {
    if (!isOpen) return null;

    const confirmClasses = tone === 'danger'
        ? (loading ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700')
        : (loading ? 'bg-emerald-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700');

    const iconWrapClasses = tone === 'danger' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600';

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => { if (!loading) onClose(); }}
        >
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${iconWrapClasses}`}>
                            {tone === 'danger' ? <AlertTriangle size={18} /> : <HelpCircle size={18} />}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
                    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                    {details}
                    {onCommentChange && (
                        <div className="space-y-1">
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider">{commentLabel}</label>
                            <textarea
                                value={commentValue || ''}
                                onChange={e => onCommentChange(e.target.value)}
                                disabled={loading}
                                placeholder={commentPlaceholder}
                                rows={3}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#1B4332] resize-none disabled:opacity-50"
                            />
                        </div>
                    )}
                </div>

                <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={loading}
                        className={`flex-1 px-4 py-2.5 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${confirmClasses}`}
                    >
                        {loading && <Loader2 size={15} className="animate-spin" />}
                        {loading ? 'Processing...' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
